
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePremiumBotDetail } from '@/hooks/use-premium-bot-detail';
import { usePremiumBotAccounts } from '@/hooks/use-premium-bot-accounts';
import { usePremiumBotUsage } from '@/hooks/use-premium-bot-usage';
import { EnhancedBotDetailHeader } from '@/components/premium-bot-detail/EnhancedBotDetailHeader';
import { EnhancedBotStatsSection } from '@/components/premium-bot-detail/EnhancedBotStatsSection';
import { EnhancedBotFeaturesSection } from '@/components/premium-bot-detail/EnhancedBotFeaturesSection';
import { EnhancedBotInfoSection } from '@/components/premium-bot-detail/EnhancedBotInfoSection';
import { EnhancedPremiumConnectedAccountsSection } from '@/components/premium-bot-detail/EnhancedPremiumConnectedAccountsSection';
import { PremiumBotSignalsSection } from '@/components/premium-bot-detail/PremiumBotSignalsSection';
import { CompletePremiumAddAccountDialog } from '@/components/premium-bot-detail/CompletePremiumAddAccountDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

const PremiumBotDetailPage = () => {
  const { t } = useTranslation();
  const { botId } = useParams<{ botId: string; }>();
  const isMobile = useIsMobile();
  
  const {
    premiumBot,
    isLoading,
    error,
    isSubscribing,
    handleSubscribe,
    resetSubscribingState,
    refetch: refetchBotDetails
  } = usePremiumBotDetail(botId);
  
  const {
    connectedAccounts,
    isLoading: isLoadingAccounts,
    error: accountsError,
    handleToggleConnection,
    handleDeleteAccount,
    refetch: refetchAccounts,
    disconnectedCspAccountId,
    isDisconnectedDialogOpen,
    setIsDisconnectedDialogOpen,
    isToggling,
    isDeleting,
    deletingAccountIds
  } = usePremiumBotAccounts(botId);
  
  const [showAddAccountDialog, setShowAddAccountDialog] = useState(false);

  // Modified: Check if the user has any accounts with API connection ID (not filtering by status anymore)
  const hasConnectedAccounts = connectedAccounts.some(account => account.api_connection_id);

  // Check if this bot is integrated (either pending or connected)
  const isIntegrated = !!premiumBot?.premiumDetails?.is_integrated;
  
  const { canUsePremiumBot, currentCount, maxCount, getErrorMessage } = usePremiumBotUsage();

  // Handle subscription - check limits before showing add account dialog
  const handleSubscribeClick = () => {
    if (!canUsePremiumBot) {
      toast.error(t("usage_limit_reached"), {
        description: getErrorMessage()
      });
      return;
    }
    const result = handleSubscribe();
    if (result.success) {
      setShowAddAccountDialog(true);
    }
  };
  
  // Enhanced success handler with proper async handling
  const handleAddAccountSuccess = async () => {
    setShowAddAccountDialog(false);
    resetSubscribingState();
    
    // Enhanced refresh with Promise.all for immediate data sync
    try {
      await Promise.all([
        refetchBotDetails(),
        refetchAccounts()
      ]);
    } catch (error) {
      console.error('Error refreshing data after account addition:', error);
      // Fallback: force a page reload if cache refresh fails
      window.location.reload();
    }
  };
  
  const handleDialogClose = () => {
    setShowAddAccountDialog(false);
    resetSubscribingState();
  };

  // Handle errors
  if (error) {
    return <div className="flex flex-col items-center justify-center h-80 space-y-4">
        <Info className="h-16 w-16 text-destructive opacity-80" />
        <h2 className="text-2xl font-medium">{t("failed_to_load_bot")}</h2>
        <p className="text-muted-foreground">{t("error_fetching_data")}</p>
        <Button onClick={() => window.location.reload()} variant="outline">{t("try_again")}</Button>
      </div>;
  }
  
  const sectionTitleClass = isMobile ? "text-lg font-semibold" : "text-xl font-semibold";
  
  return <div className="space-y-8 md:space-y-10 pb-8 md:pb-10">
      <Toaster position="top-right" />

      {/* Enhanced Header */}
      <div data-tour="detail-description">
        <EnhancedBotDetailHeader bot={premiumBot} isLoading={isLoading} isIntegrated={isIntegrated} onSubscribe={handleSubscribeClick} isSubscribing={isSubscribing} canUsePremiumBot={canUsePremiumBot} currentCount={currentCount} maxCount={maxCount} errorMessage={getErrorMessage()} />
      </div>

      {/* Enhanced Connected Accounts Section - Only show if user has integrated with this bot and has connected accounts */}
      {isIntegrated && hasConnectedAccounts && <div className="space-y-4" data-tour="detail-connected-accounts">
          <EnhancedPremiumConnectedAccountsSection 
            accounts={connectedAccounts.filter(acc => acc.api_connection_id)} 
            isLoading={isLoadingAccounts} 
            error={accountsError} 
            botId={botId || ''} 
            onToggleConnection={handleToggleConnection} 
            onDeleteAccount={handleDeleteAccount} 
            deletingAccountIds={deletingAccountIds}
            disconnectedCspAccountId={disconnectedCspAccountId} 
            isDisconnectedDialogOpen={isDisconnectedDialogOpen} 
            setIsDisconnectedDialogOpen={setIsDisconnectedDialogOpen} 
          />
        </div>}

      {/* Enhanced Key Stats Section */}
      <div className="space-y-4" data-tour="detail-stats">
        <EnhancedBotStatsSection premiumDetails={premiumBot?.premiumDetails} isLoading={isLoading} />
      </div>

      {/* Signal History - Only show if user has integrated with this bot and has connected accounts */}
      {isIntegrated && hasConnectedAccounts && <div className="space-y-4" data-tour="detail-connected-accounts">
          
          <PremiumBotSignalsSection botId={botId} isIntegrated={isIntegrated} />
        </div>}

      {/* Enhanced Features and Information Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div data-tour="detail-features">
          <EnhancedBotFeaturesSection features={premiumBot?.premiumDetails?.features} isLoading={isLoading} />
        </div>
        <div data-tour="detail-info">
          <EnhancedBotInfoSection bot={premiumBot} isLoading={isLoading} />
        </div>
      </div>

      {/* Add Account Dialog Triggers */}
      <div data-tour="detail-use-bot" />
      <div data-tour="detail-multiplier" />

      {/* Add Account Dialog */}
      {botId && <CompletePremiumAddAccountDialog isOpen={showAddAccountDialog} onClose={handleDialogClose} botId={botId} onSuccess={handleAddAccountSuccess} />}
    </div>;
};

export default PremiumBotDetailPage;
